import React, { FunctionComponent, ReactNode } from "react";
import classNames from "classnames";
import { Checkbox } from "@hxnova/react-components";
import { Icon } from "@hxnova/icons";

import "../../../css/shared/trade_tree.css";
import { tradeNodeName, tradeRowIndent } from "../../services/utilities/trade_tree";

import type { BreakdownNode } from "../../services/utilities/breakdown";

export type Props = {
  nodes: BreakdownNode[]
  leavesByCode: Record<string, string[]>
  selectedCodes: Set<string>
  expandedCodes: Set<string>
  isSearching: boolean
  onToggle: (code: string, on: boolean) => void
  onToggleExpand: (code: string) => void
  isDisabled?: (code: string) => boolean
  renderAdornment?: (node: BreakdownNode) => ReactNode
};

export const tradeRowTestId = (code: string): string => `TradeTree-row-${code}`;

const TradeTreeRows: FunctionComponent<Props> = function TradeTreeRows(props) {
  const { nodes, leavesByCode, selectedCodes, expandedCodes, isSearching, onToggle, onToggleExpand, isDisabled, renderAdornment } = props;

  return <>{nodes.map(node => {
    const code = node.id as string;
    const isLeaf = !node.children?.length;
    const isSubGroup = !isLeaf && node.level >= 2;
    const disabled = !!isDisabled?.(code);
    const leaves = leavesByCode[code] ?? [];
    const selectedLeaves = leaves.reduce((count, leaf) => count + (selectedCodes.has(leaf) ? 1 : 0), 0);
    const checked = leaves.length > 0 && selectedLeaves === leaves.length;
    const indeterminate = selectedLeaves > 0 && !checked;
    const isCollapsed = !isLeaf && !isSearching && !expandedCodes.has(code);
    const name = tradeNodeName(node);

    return <React.Fragment key={code}>
      {/* In the row, not the checkbox's label, so a wrapping name grows the row. */}
      <div
        className={classNames("TradeTree-row", {
          "TradeTree-row--group": isSubGroup,
          "TradeTree-row--selected": checked,
          "TradeTree-row--disabled": disabled
        })}
        style={{ paddingLeft: tradeRowIndent(node.level, isLeaf) }}
        data-testid={tradeRowTestId(code)}
        onClick={event => {
          // The checkbox handles its own clicks; acting on them here too would toggle twice.
          if (disabled || (event.target as HTMLElement).closest(".TradeTree-option")) {
            return;
          }
          onToggle(code, !checked);
        }}
      >
        {!isLeaf && <button
          type="button"
          className="TradeTree-toggle"
          aria-expanded={!isCollapsed}
          aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${name}`}
          onClick={event => {
            event.stopPropagation();
            onToggleExpand(code);
          }}
        >
          <Icon family="material" name={isCollapsed ? "keyboard_arrow_right" : "keyboard_arrow_down"} size={20}/>
        </button>}
        <Checkbox
          className="TradeTree-option"
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          onChange={() => onToggle(code, !checked)}
          slotProps={{ input: { "aria-label": name } }}
        />
        <span className="TradeTree-text">
          <span className={classNames("TradeTree-title", { "TradeTree-title--group": !isLeaf })}>{name}</span>
          <span className="TradeTree-code">{code}</span>
        </span>
        {renderAdornment?.(node)}
      </div>
      {!isLeaf && !isCollapsed && <TradeTreeRows {...props} nodes={node.children}/>}
    </React.Fragment>;
  })}</>;
};

export default TradeTreeRows;
